<template>
  <div class="row">
    <div class="col-md-12 col-xxl-12 col-sm-12 mb-10">
      <div class="card mb-25 border-0 rounded-0">
        <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
          <div class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25">
            <h4 class="position-relative text-black fw-bold mb-10">Détails de la créance</h4>
            <router-link to="/creances/liste-creances" class="btn btn-primary transition border-0 lh-1 fw-medium">
              <i class="flaticon-left-arrow lh-1 me-1 position-relative top-2"></i>
              <span class="position-relative">Retour</span>
            </router-link>
          </div>
          <div class="row gx-3">
            <!-- Left Column -->
            <div class="col-md-6">
              <table class="table table-borderless">
                <tbody>
                  <tr v-if="creance?.refCrean">
                    <td class="fw-semibold">Référence de la créance :</td>
                    <td>{{ creance.refCrean }}</td>
                  </tr>
                  <tr v-if="creance?.date">
                    <td class="fw-semibold">Date de la créance :</td>
                    <td>{{ format_Date(creance.date) }}</td>
                  </tr>
                  <tr v-if="creance?.client">
                    <td class="fw-semibold">Client :</td>
                    <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                      {{ creance?.client.nomClient && creance?.client.prenomClient ? `${creance?.client.nomClient} ${creance?.client.prenomClient}` : creance?.client.raisonSociale }}
                    </td>
                  </tr>
                  <tr v-if="creance?.montant">
                    <td class="fw-semibold">Montant total de la créance :</td>
                    <td>{{ separateur(creance.montant) }}</td>
                  </tr>
                  <tr v-if="creance?.reste_a_payer !== undefined">
                    <td class="fw-semibold">Reste à payer :</td>
                    <td>{{ separateur(creance.reste_a_payer) }}</td>
                  </tr>
                  <tr v-if="creance?.motifCreance">
                    <td class="fw-semibold">Motif de la créance :</td>
                    <td>{{ creance.motifCreance }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <!-- Right Column -->
            <div class="col-md-6">
              <table class="table table-borderless">
                <tbody>
                  <tr v-if="creance?.refCommande">
                    <td class="fw-semibold">Références des commandes :</td>
                    <td>{{ creance.refCommande }}</td>
                  </tr>
                  <tr v-if="creance?.montantTotalCommandes">
                    <td class="fw-semibold">Montant total des commandes :</td>
                    <td>{{ separateur(creance.montantTotalCommandes) }}</td>
                  </tr>
                  <tr v-if="creance?.montantRegle">
                    <td class="fw-semibold">Montant réglé :</td>
                    <td>{{ separateur(creance.montantRegle) }}</td>
                  </tr>
                  <tr v-if="creance?.dureeCreance">
                    <td class="fw-semibold">Durée de créance (jours) :</td>
                    <td>{{ creance.dureeCreance }}</td>
                  </tr>
                  <tr v-if="creance?.modeReglement">
                    <td class="fw-semibold">Mode de règlement :</td>
                    <td>{{ creance?.modeReglement.libelle }}</td>
                  </tr>
                  <tr v-if="creance?.autresinfos">
                    <td class="fw-semibold">Autres informations :</td>
                    <td>{{ creance.autresinfos }}</td>
                  </tr>
                  <tr v-if="creance?.nbreEcheance">
                    <td class="fw-semibold">Nombre d'échéances :</td>
                    <td>{{ creance.nbreEcheance }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <!-- Échéances Section (Full Width) -->
            <div class="col-md-12" v-if="creance?.echeances?.length">
              <h5 class="mt-3">Échéances</h5>
              <table class="table table-bordered table-striped">
                <thead>
                  <tr>
                    <th>Date Échéance</th>
                    <th>Montant</th>
                    <th>Reste à payer</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(echeance, index) in creance.echeances" :key="index">
                    <td>{{ format_Date(echeance.date) }}</td>
                    <td>{{ separateur(echeance.montant) }}</td>
                    <td>{{ separateur(echeance.reste_a_payer) }}</td>
                    <td>
                      <span :class="['badge', getStatutBadge(echeance).class]">{{ getStatutBadge(echeance).text }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="col-md-12" v-if="creance?.commandes?.length">
              <h5 class="mt-3">Commandes liées à cette créance</h5>
              <table class="table table-bordered table-striped">
                <thead>
                  <tr>
                    <th>Référence</th>
                    <th>Date Commande</th>
                    <th>Montant TTC</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(commande, index) in creance.commandes" :key="index">
                    <td>{{ commande.refCom }}</td>
                    <td>{{ format_Date(commande.dateCommande) }}</td>
                    <td>{{ separateur(commande.montTTC) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from "vue";
import ApiService from "@/services/ApiService";
import { error, format_date, format_Date, separateur } from "@/utils/utils";
import { useRoute } from "vue-router";

export default defineComponent({
  name: "ViewCreance",
  setup: () => {
    const route = useRoute();
    const creance = ref<any>(null);
    const clientOptions = ref<any[]>([]);
    const modeReglementOptions = ref<any[]>([]);

    // Récupérer les détails de la créance
    function getCreance(id: string) {
      return ApiService.get("/creances/", id)
        .then(({ data }) => {
          creance.value = data.data;
        })
        .catch(({ response }) => {
          error(response?.data?.message || "Erreur lors de la récupération de la créance");
        });
    }

    // Charger les clients pour afficher le nom
    const getAllClients = async () => {
      try {
        const response = await ApiService.get('/all/clients');
        const clientsData = response.data.data;
        clientOptions.value = clientsData.map((client: any) => ({
          value: client.id,
          label: client.nomClient && client.prenomClient
            ? `${client.nomClient} ${client.prenomClient}`
            : `${client.raisonSociale}`,
        }));
      } catch (err) {
        error(err.message);
      }
    };

    onMounted(() => {
      const id = route.params.id as string;
      getCreance(id);
      getAllClients();
    });

    const getStatutBadge = (echeance: any) => {
      const today = new Date();
      const echeanceDate = new Date(echeance.date);

      if (echeance.reste_a_payer === 0) {
        return { class: 'badge badge-light-success', text: 'Payé' };
      }

      // 2. En attente (date dans le futur, aucun paiement partiel)
      if (echeanceDate > today && echeance.reste_a_payer === echeance.montant) {
        return { class: 'badge badge-light-warning', text: 'En attente' };
      }

      // 3. En cours (paiement partiel, avant ou après la date)
      if (echeance.reste_a_payer > 0 && echeance.reste_a_payer < echeance.montant) {
        return { class: 'badge badge-light-info', text: 'En cours' };
      }

      // 4. Impayé (date dépassée, aucun paiement)
      if (echeanceDate < today && echeance.reste_a_payer === echeance.montant) {
        return { class: 'badge badge-light-danger', text: 'Impayé' };
      }

      // Par défaut
      return { class: 'badge badge-light-secondary', text: 'Inconnu' };
    };

    return {
      creance,
      format_date,
      format_Date,
      separateur,
      getStatutBadge
    };
  },
});
</script>

<style scoped>
.table-borderless td {
  padding: 10px 15px;
  vertical-align: middle;
}
.table-borderless .fw-semibold {
  width: 40%;
}
.table-bordered {
  margin-top: 15px;
}
@media (max-width: 767px) {
  .table-borderless td {
    display: block;
    width: 100%;
  }
  .table-borderless .fw-semibold {
    width: 100%;
    margin-bottom: 5px;
  }
}
</style>